import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

const AUDIT_QUESTION_BASE_URL = `${AUDIT_BASE_URL}/questions`

class AuditQuestionService {
  /**
   * Create a new audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {Object} questionData - The audit question data
   */
  async createAuditQuestion(accessToken, auditPlanId, sectionId, questionData) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(questionData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit question creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Fetch all audit questions for a specific audit section with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getAuditQuestions(accessToken, auditPlanId, sectionId, filters = {}, paginationData = {}) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.interview_result) {
        queryParams.append('interview_result', filters.interview_result);
      }
      if (filters.auditee_confirmation) {
        queryParams.append('auditee_confirmation', filters.auditee_confirmation);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit questions',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get detailed information about a specific audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   */
  async getAuditQuestionDetail(accessToken, auditPlanId, sectionId, questionId) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit question details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Update an existing audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   * @param {Object} questionData - The updated audit question data
   */
  async updateAuditQuestion(accessToken, auditPlanId, sectionId, questionId, questionData, auditQuestionRisksToRemove) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      console.log('🔍 AUDIT QUESTION UPDATE DEBUG:');
      console.log('Current question risks (AuditQuestionRisk):', questionData.current_question_risks);
      console.log('New audit plan risks to add (AuditPlanRisk):', questionData.new_auditplan_risks_to_add);
      console.log('AuditQuestionRisks to remove:', auditQuestionRisksToRemove);
      
      // Transform frontend data to backend format
      const backendData = {
        ...questionData,
        // Remove the frontend-specific fields
        current_question_risks: undefined,
        new_auditplan_risks_to_add: undefined,
        // Add the new risk management fields
        auditquestion_risks_to_remove: Array.from(auditQuestionRisksToRemove), // Array of AuditQuestionRisk IDs to remove
        auditplan_risks_to_add: (questionData.new_auditplan_risks_to_add || []).map(r => r.id), // Array of AuditPlanRisk IDs to add
        current_question_risks_updates: (questionData.current_question_risks || []).map(r => ({
          id: r.id,
          probability: r.probability,
          consequence: r.consequence
        })) // Array of current question risk updates with id, probability, and consequence
      };
      
      console.log('📤 Sending to backend:', backendData);

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/edit/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit question update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Partially update an existing audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   * @param {Object} questionData - The partial audit question data
   */
  async patchAuditQuestion(accessToken, auditPlanId, sectionId, questionId, questionData) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/edit/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(questionData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit question update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Delete an audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   */
  async deleteAuditQuestion(accessToken, auditPlanId, sectionId, questionId) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit question deletion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ===== ATTACHMENT METHODS =====

  /**
   * Upload an attachment for an audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   * @param {FormData} formData - Form data containing the file and metadata
   */
  async uploadQuestionAttachment(accessToken, auditPlanId, sectionId, questionId, formData) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/attachments/upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'File upload failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Delete an attachment from an audit question
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   * @param {number} attachmentId - The attachment ID
   */
  async deleteQuestionAttachment(accessToken, auditPlanId, sectionId, questionId, attachmentId) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/attachments/${attachmentId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Attachment deletion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ===== AUDITEE CONFIRMATION METHODS =====

  /**
   * Confirm an audit question response (for auditees)
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The section ID (required)
   * @param {number} questionId - The audit question ID
   * @param {string} confirmationStatus - The confirmation status (confirmed, rejected, needs_clarification, pending)
   */
  async confirmAuditQuestion(accessToken, auditPlanId, sectionId, questionId, confirmationStatus) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      if (!sectionId) {
        return {
          success: false,
          status: 400,
          error: 'Section ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/confirm/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          auditee_response_confirmation: confirmationStatus
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Question confirmation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit questions that need confirmation by the current auditee
   * @param {string} accessToken - JWT access token
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getQuestionsForConfirmation(accessToken, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.audit_section) {
        queryParams.append('audit_section', filters.audit_section);
      }
      if (filters.confirmation_status) {
        queryParams.append('confirmation_status', filters.confirmation_status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_QUESTION_BASE_URL}/for-confirmation/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching questions for confirmation',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Add risk to audit question
   */
  async addRiskToQuestion(accessToken, auditPlanId, sectionId, questionId, riskData) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/risks/add/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(riskData)
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: data
        };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to add risk to question',
          errorData: data
        };
      }
    } catch (error) {
      console.error('Error adding risk to question:', error);
      return {
        success: false,
        error: 'Network error occurred while adding risk to question'
      };
    }
  }

  /**
   * Remove risk from audit question
   */
  async removeRiskFromQuestion(accessToken, auditPlanId, sectionId, questionId, questionRiskId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/risks/${questionRiskId}/remove/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        return {
          success: true,
          message: 'Risk removed from question successfully'
        };
      } else {
        const data = await response.json();
        return {
          success: false,
          error: data.error || 'Failed to remove risk from question',
          errorData: data
        };
      }
    } catch (error) {
      console.error('Error removing risk from question:', error);
      return {
        success: false,
        error: 'Network error occurred while removing risk from question'
      };
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Helper method to create FormData for file uploads
   * @param {File} file - The file to upload
   * @param {string} fileName - Optional custom file name
   * @param {string} description - Optional file description
   */
  createAttachmentFormData(file, fileName = null, description = '') {
    const formData = new FormData();
    formData.append('file', file);
    
    // Always provide file_name - use custom name or file's original name
    const finalFileName = fileName || file.name;
    formData.append('file_name', finalFileName);
    
    if (description) {
      formData.append('description', description);
    }
    
    return formData;
  }

  /**
   * Helper method to format question data for API submission
   * @param {Object} questionData - Raw question data from form
   */
  formatQuestionData(questionData) {
    const formatted = { ...questionData };
    
    // Format related objects arrays
    if (questionData.initial_observations && Array.isArray(questionData.initial_observations)) {
      formatted.initial_observations_data = questionData.initial_observations.map(obs => ({
        observation: obs.observation || obs
      }));
      delete formatted.initial_observations;
    }
    
    if (questionData.kpis && Array.isArray(questionData.kpis)) {
      formatted.kpis_data = questionData.kpis.map(kpi => ({
        kpi_name: kpi.kpi_name || kpi.name,
        kpi_value: kpi.kpi_value || kpi.value,
        kpi_target: kpi.kpi_target || kpi.target,
        kpi_description: kpi.kpi_description || kpi.description
      }));
      delete formatted.kpis;
    }
    
    if (questionData.observations && Array.isArray(questionData.observations)) {
      formatted.observations_data = questionData.observations.map(obs => ({
        observation: obs.observation || obs
      }));
      delete formatted.observations;
    }
    
    if (questionData.recommendations && Array.isArray(questionData.recommendations)) {
      formatted.recommendations_data = questionData.recommendations.map(rec => ({
        recommendation: rec.recommendation || rec.text || rec,
        priority: rec.priority || 'medium'
      }));
      delete formatted.recommendations;
    }
    
    return formatted;
  }
}

const auditQuestionService = new AuditQuestionService();
export default auditQuestionService;